import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from 'components/Box';
import Button from 'components/Button';
import { Chevron as ChevronIcon } from 'components/icons';
import Text from 'components/Text';
import TextLink from 'components/TextLink';
import Dropdown from 'components/Dropdown';
import JumpToPageWidget from './JumpToPageWidget';
var NavButtons = styled.div.withConfig({
  displayName: "Pagination__NavButtons",
  componentId: "sc-1ontn7v-0"
})(["display:flex;flex-wrap:wrap;align-self:stretch;justify-content:space-between;"]);
var StyledChevron = styled(ChevronIcon).withConfig({
  displayName: "Pagination__StyledChevron",
  componentId: "sc-1ontn7v-1"
})(["color:", ";"], function (p) {
  return p.theme.links;
});
export function clampToPageRange(page, pageCount) {
  return Math.min(pageCount, Math.max(1, page));
}
function OptionalDropdownWidget(_ref) {
  var children = _ref.children,
    content = _ref.content,
    hasDropdown = _ref.hasDropdown;
  if (!hasDropdown) return children;
  return /*#__PURE__*/React.createElement(Dropdown, {
    renderInPlace: true,
    allowTooltipClicks: true,
    content: content
  }, function (dropdown) {
    return /*#__PURE__*/React.createElement(TextLink, {
      stealthy: true,
      as: "button",
      ref: dropdown.ref,
      onClick: dropdown.toggle,
      "aria-expanded": dropdown.isOpen ? 'true' : 'false'
    }, children, /*#__PURE__*/React.createElement(StyledChevron, null));
  });
}
function PaginationLink(_ref2) {
  var children = _ref2.children,
    to = _ref2.to,
    isDisabled = _ref2.isDisabled,
    size = _ref2.size;
  return /*#__PURE__*/React.createElement(Button, {
    as: Link,
    to: to,
    variant: "transparent",
    size: size,
    isDisabled: isDisabled
  }, children);
}
export function getPaginationLinks(_ref3) {
  var pageCount = _ref3.pageCount,
    currentPage = _ref3.currentPage,
    getPageLink = _ref3.getPageLink;
  var hasPrevLink = currentPage > 1;
  var hasNextLink = currentPage < pageCount;
  return {
    prevPageLink: hasPrevLink ? getPageLink(currentPage - 1) : null,
    nextPageLink: hasNextLink ? getPageLink(currentPage + 1) : null
  };
}
function Pagination(_ref4) {
  var _ref4$pageCount = _ref4.pageCount,
    pageCount = _ref4$pageCount === void 0 ? 1 : _ref4$pageCount,
    _ref4$currentPage = _ref4.currentPage,
    currentPage = _ref4$currentPage === void 0 ? 1 : _ref4$currentPage,
    _ref4$getPageLink = _ref4.getPageLink,
    getPageLink = _ref4$getPageLink === void 0 ? function (pageNumber) {
      return "?page=".concat(pageNumber);
    } : _ref4$getPageLink,
    _ref4$buttonSize = _ref4.buttonSize,
    buttonSize = _ref4$buttonSize === void 0 ? 'medium' : _ref4$buttonSize,
    _ref4$desktopViewBrea = _ref4.desktopViewBreakpoint,
    desktopViewBreakpoint = _ref4$desktopViewBrea === void 0 ? 'xxs' : _ref4$desktopViewBrea;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _getPaginationLinks = getPaginationLinks({
      pageCount: pageCount,
      currentPage: currentPage,
      getPageLink: getPageLink
    }),
    prevPageLink = _getPaginationLinks.prevPageLink,
    nextPageLink = _getPaginationLinks.nextPageLink;
  var breakpoints = !desktopViewBreakpoint ? undefined : desktopViewBreakpoint;
  return /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    alignItems: "center",
    flexDirection: ['column-reverse', 'row'],
    breakpoints: breakpoints,
    width: "100%"
  }, /*#__PURE__*/React.createElement(Box, {
    grow: true,
    shrink: true,
    dimmed: true,
    fontSize: "s"
  }, /*#__PURE__*/React.createElement(Text, {
    dimmed: true
  }, /*#__PURE__*/React.createElement(OptionalDropdownWidget, {
    hasDropdown: pageCount > 1,
    content: function content(dropdown) {
      return /*#__PURE__*/React.createElement(JumpToPageWidget, {
        pageCount: pageCount,
        currentPage: currentPage,
        getPageLink: getPageLink,
        onClose: dropdown.close
      });
    }
  }, t('pagination_current_page_of_total', {
    count: pageCount,
    current: clampToPageRange(currentPage, pageCount)
  })))), pageCount > 1 && /*#__PURE__*/React.createElement(NavButtons, null, /*#__PURE__*/React.createElement(PaginationLink, {
    isDisabled: !prevPageLink,
    to: prevPageLink,
    size: buttonSize
  }, t('pagination_previous')), /*#__PURE__*/React.createElement(PaginationLink, {
    isDisabled: !nextPageLink,
    to: nextPageLink,
    size: buttonSize
  }, t('pagination_next'))));
}
export default Pagination;